import { z } from 'zod';
import { LedgerReason, SubscriptionStatus, Tier } from './enums.ts';

/**
 * Billing summary WIRE CONTRACT — the single Zod-backed source of truth for the
 * `GET /billing/summary` payload, shared api ↔ backend ↔ frontend (CLAUDE.md:
 * "Validate shared contracts with Zod schemas in `@4f/shared`; use typed
 * contracts throughout the API, backend, and frontend"). The backend `to*View`
 * mappers PRODUCE these shapes; the frontend RE-VALIDATES them at the network
 * boundary (`apps/frontend/src/api/client.ts`). Defining the schema once here
 * means backend and frontend cannot drift. Money stays in micros end to end;
 * the client formats micros → a display credit count.
 */

/** One statement line — a signed ledger entry, most-recent first. */
export const LedgerEntryView = z.object({
  id: z.string().min(1),
  reason: LedgerReason,
  deltaMicros: z.number(),
  balanceAfterMicros: z.number(),
  createdAt: z.string(),
});
export type LedgerEntryView = z.infer<typeof LedgerEntryView>;

/** The active (or most recent) subscription, if any. */
export const SubscriptionView = z.object({
  id: z.string().min(1),
  tier: Tier,
  status: SubscriptionStatus,
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
});
export type SubscriptionView = z.infer<typeof SubscriptionView>;

/** One tier row of the plan ladder — config, not a feature flag (billing.md § "Tiers"). */
export const PlanView = z.object({
  tier: Tier,
  monthlyCreditsMicros: z.number(),
  current: z.boolean(),
});
export type PlanView = z.infer<typeof PlanView>;

/** One top-up pack of the catalog. */
export const PackView = z.object({
  id: z.string().min(1),
  priceUsd: z.number(),
  creditsMicros: z.number(),
});
export type PackView = z.infer<typeof PackView>;

/** The whole billing summary the client renders verbatim — display-only; the api is authority. */
export const BillingSummaryView = z.object({
  tier: Tier,
  creditBalanceMicros: z.number(),
  subscription: SubscriptionView.nullable(),
  recentLedger: z.array(LedgerEntryView),
  plans: z.array(PlanView),
  packs: z.array(PackView),
});
export type BillingSummaryView = z.infer<typeof BillingSummaryView>;
